import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Advanced Analytics Store
 * Manages state for the Advanced Analytics & Alerts page
 */

const initialFilters = {
  timeRange: { start: null, end: null, period: '30' },
  operations: [],
  suppliers: [],
  materials: [],
  performance: { minEfficiency: 0, maxEfficiency: 100 },
  locations: [],
  stages: [],
  status: []
};

const useAnalyticsStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ==========================================
        // STATE
        // ==========================================

        // Active tab
        activeTab: 'operations',

        // Date range
        dateRange: '30',

        // Filters
        filters: initialFilters,

        // Applied filters (what's actually being used for queries)
        appliedFilters: initialFilters,

        // Filter panel visibility
        showFilters: true,

        // Saved views
        savedViews: [],
        activeSavedView: null,

        // Alert statistics
        alertCounts: {
          CRITICAL: 0,
          HIGH: 0,
          MEDIUM: 0,
          LOW: 0,
          total: 0
        },

        // Alert instances
        activeAlerts: [],
        alertsLoading: false,

        // Loading states
        isLoadingViews: false,
        isApplyingFilters: false,

        // ==========================================
        // ACTIONS - Tab Management
        // ==========================================

        /**
         * Set the active tab
         * @param {string} tab - Tab ID to activate
         */
        setActiveTab: (tab) => set({ activeTab: tab }),

        /**
         * Set date range
         * @param {string} range - Date range value
         */
        setDateRange: (range) => set({ dateRange: range }),

        // ==========================================
        // ACTIONS - Filter Management
        // ==========================================

        /**
         * Update filters (doesn't apply them yet)
         * @param {Object} newFilters - Partial filter object
         */
        updateFilters: (newFilters) =>
          set((state) => ({
            filters: { ...state.filters, ...newFilters }
          })),

        /**
         * Set complete filters object
         * @param {Object} filters - Complete filter object
         */
        setFilters: (filters) => set({ filters }),

        /**
         * Apply current filters (copies filters to appliedFilters)
         */
        applyFilters: () =>
          set((state) => ({
            appliedFilters: { ...state.filters },
            isApplyingFilters: false
          })),

        /**
         * Reset filters to initial state
         */
        resetFilters: () =>
          set({
            filters: initialFilters,
            appliedFilters: initialFilters
          }),

        /**
         * Toggle filter panel visibility
         */
        toggleFilters: () => set((state) => ({ showFilters: !state.showFilters })),

        /**
         * Set filter panel visibility
         * @param {boolean} visible - Visibility state
         */
        setShowFilters: (visible) => set({ showFilters: visible }),

        /**
         * Set applying filters loading state
         * @param {boolean} loading - Loading state
         */
        setApplyingFilters: (loading) => set({ isApplyingFilters: loading }),

        // ==========================================
        // ACTIONS - Saved Views
        // ==========================================

        /**
         * Set saved views list
         * @param {Array} views - Array of saved views
         */
        setSavedViews: (views) => set({ savedViews: views }),

        /**
         * Add a new saved view
         * @param {Object} view - Saved view object
         */
        addSavedView: (view) =>
          set((state) => ({
            savedViews: [...state.savedViews, view]
          })),

        /**
         * Update a saved view
         * @param {number} id - View ID
         * @param {Object} updates - Updated fields
         */
        updateSavedView: (id, updates) =>
          set((state) => ({
            savedViews: state.savedViews.map((view) =>
              view.id === id ? { ...view, ...updates } : view
            )
          })),

        /**
         * Remove a saved view
         * @param {number} id - View ID to remove
         */
        removeSavedView: (id) =>
          set((state) => ({
            savedViews: state.savedViews.filter((view) => view.id !== id),
            activeSavedView: state.activeSavedView?.id === id ? null : state.activeSavedView
          })),

        /**
         * Set active saved view
         * @param {Object} view - View to activate
         */
        setActiveSavedView: (view) =>
          set({
            activeSavedView: view,
            filters: view?.filter_config || initialFilters,
            appliedFilters: view?.filter_config || initialFilters
          }),

        /**
         * Clear active saved view
         */
        clearActiveSavedView: () => set({ activeSavedView: null }),

        /**
         * Set loading state for saved views
         * @param {boolean} loading - Loading state
         */
        setLoadingViews: (loading) => set({ isLoadingViews: loading }),

        // ==========================================
        // ACTIONS - Alerts
        // ==========================================

        /**
         * Set alert counts
         * @param {Object} counts - Alert counts by severity
         */
        setAlertCounts: (counts) => set({ alertCounts: counts }),

        /**
         * Update a specific alert count
         * @param {string} severity - Alert severity
         * @param {number} count - New count
         */
        updateAlertCount: (severity, count) =>
          set((state) => ({
            alertCounts: {
              ...state.alertCounts,
              [severity]: count,
              total:
                severity === 'total'
                  ? count
                  : Object.keys(state.alertCounts)
                      .filter((key) => key !== 'total' && key !== severity)
                      .reduce((sum, key) => sum + state.alertCounts[key], 0) + count
            }
          })),

        /**
         * Increment alert count for a severity
         * @param {string} severity - Alert severity
         */
        incrementAlertCount: (severity) =>
          set((state) => ({
            alertCounts: {
              ...state.alertCounts,
              [severity]: state.alertCounts[severity] + 1,
              total: state.alertCounts.total + 1
            }
          })),

        /**
         * Decrement alert count for a severity
         * @param {string} severity - Alert severity
         */
        decrementAlertCount: (severity) =>
          set((state) => ({
            alertCounts: {
              ...state.alertCounts,
              [severity]: Math.max(0, state.alertCounts[severity] - 1),
              total: Math.max(0, state.alertCounts.total - 1)
            }
          })),

        /**
         * Set active alerts
         * @param {Array} alerts - Array of active alert instances
         */
        setActiveAlerts: (alerts) => set({ activeAlerts: alerts }),

        /**
         * Add an active alert
         * @param {Object} alert - Alert instance
         */
        addActiveAlert: (alert) =>
          set((state) => ({
            activeAlerts: [alert, ...state.activeAlerts]
          })),

        /**
         * Update an active alert
         * @param {number} id - Alert instance ID
         * @param {Object} updates - Updated fields
         */
        updateActiveAlert: (id, updates) =>
          set((state) => ({
            activeAlerts: state.activeAlerts.map((alert) =>
              alert.id === id ? { ...alert, ...updates } : alert
            )
          })),

        /**
         * Remove an active alert
         * @param {number} id - Alert instance ID
         */
        removeActiveAlert: (id) =>
          set((state) => ({
            activeAlerts: state.activeAlerts.filter((alert) => alert.id !== id)
          })),

        /**
         * Set alerts loading state
         * @param {boolean} loading - Loading state
         */
        setAlertsLoading: (loading) => set({ alertsLoading: loading }),

        // ==========================================
        // UTILITY ACTIONS
        // ==========================================

        /**
         * Reset entire store to initial state
         */
        resetStore: () =>
          set({
            activeTab: 'operations',
            dateRange: '30',
            filters: initialFilters,
            appliedFilters: initialFilters,
            showFilters: true,
            savedViews: [],
            activeSavedView: null,
            alertCounts: {
              CRITICAL: 0,
              HIGH: 0,
              MEDIUM: 0,
              LOW: 0,
              total: 0
            },
            activeAlerts: [],
            alertsLoading: false,
            isLoadingViews: false,
            isApplyingFilters: false
          }),

        /**
         * Get current filter state
         * @returns {Object} Current filters
         */
        getCurrentFilters: () => get().appliedFilters,

        /**
         * Check if filters have changed
         * @returns {boolean} True if filters differ from applied filters
         */
        hasUnappliedFilters: () => {
          const { filters, appliedFilters } = get();
          return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
        }
      }),
      {
        name: 'advanced-analytics-storage',
        // Only persist certain fields
        partialize: (state) => ({
          activeTab: state.activeTab,
          dateRange: state.dateRange,
          showFilters: state.showFilters,
          activeSavedView: state.activeSavedView
        })
      }
    ),
    { name: 'AdvancedAnalytics' }
  )
);

export default useAnalyticsStore;
